import { get } from 'lodash';
import { setTags } from 'store/pageMeta/actions';

// Converts Gateway SEO Response into page meta tags on both server and client
export default (store) =>
	(next) =>
	(action = {}) => {
		const meta =
			get(action, 'payload.meta') || get(action, 'payload.result.meta');
		const seo =
			get(action, 'payload.seo') || get(action, 'payload.result.seo');
		const location =
			get(action, 'payload.location') ||
			get(action, 'payload.result.location');
		const isFiltered = get(meta, 'filteredPage', false);

		// Process metadata on both server and client to support dynamic updates
		const { app, pageMeta: currentPageMeta } = store.getState();
		const existingTags = currentPageMeta.tags || {};

		if (action.payload && (meta || seo)) {
			// When the page is filtered, strip any carried-forward canonical link
			// from existing state — otherwise the canonical from the unfiltered
			// page load persists even though we skip adding a new one. (SF-19077)
			const existingLinks = [...(existingTags.links || [])];
			const links = isFiltered
				? existingLinks.filter((link) => link.rel !== 'canonical')
				: existingLinks;

			const pageMeta = {
				title: existingTags.title || null,
				meta: { ...(existingTags.meta || {}) },
				og: { ...(existingTags.og || {}) },
				twitter: { ...(existingTags.twitter || {}) },
				links,
				structuredData: existingTags.structuredData || null,
				filteredPage: isFiltered,
			};

			const title = get(meta, 'title');
			const description = get(meta, 'description');
			const structuredData = get(meta, 'structuredData');
			// Filtered pages (e.g. search with amenity/size filters applied) must NOT
			// have canonical tags — only noindex,nofollow. The PageMetadata component
			// renders the robots tag when filteredPage is true. See: docs/seo-meta-tags.md
			const canonicalWithQueryParams = isFiltered ? null : get(seo, 'canonicalWithQueryParams');
			const canonical = isFiltered ? null : get(seo, 'canonical');

			const prev = get(seo, 'prev');
			const next = get(seo, 'next');
			const noindex = get(meta, 'noindex', false);

			if (title) {
				pageMeta.title = title;
				pageMeta.og.title = title;
				pageMeta.twitter.title = title;
			}

			if (description) {
				pageMeta.meta.description = description;
				pageMeta.og.description = description;
				pageMeta.twitter.description = description;
			}

			if (structuredData) {
				pageMeta.structuredData = structuredData;

				if (structuredData.description) {
					pageMeta.structuredData = {
						...pageMeta.structuredData,
						description: encodeURIComponent(
							structuredData.description || ''
						),
					};
				}
			}

			// Use canonicalWithQueryParams if available (for paginated pages), otherwise use canonical
			const canonicalUrl = canonicalWithQueryParams || canonical;
			if (canonicalUrl) {
				// Ensure URL starts with / for proper concatenation
				const normalizedUrl = canonicalUrl.startsWith('/') ? canonicalUrl : `/${canonicalUrl}`;
				// Only add baseUrl if URL is not already absolute
				const fullUrl = normalizedUrl.startsWith('http') ? normalizedUrl : `${app.baseUrl}${normalizedUrl}`;

				pageMeta.og.url = fullUrl;
				// Replace existing canonical link if any, otherwise add new one
				const canonicalIndex = pageMeta.links.findIndex(
					(link) => link.rel === 'canonical'
				);
				const canonicalLink = {
					rel: 'canonical',
					href: fullUrl,
				};
				if (canonicalIndex >= 0) {
					pageMeta.links[canonicalIndex] = canonicalLink;
				} else {
					pageMeta.links.push(canonicalLink);
				}
			}

			// Set Open Graph defaults
			if (!pageMeta.og.type) {
				pageMeta.og.type = 'website';
			}
			// Set default og:image if not already set (matches hardcoded value in PageMetadata component)
			if (!pageMeta.og.image) {
				pageMeta.og.image = `${app.baseUrl}/static/images/logos/spareFoot-social-logo.webp`;
			}

			// Set Twitter Card defaults
			if (!pageMeta.twitter.card) {
				pageMeta.twitter.card = 'summary_large_image';
			}
			if (!pageMeta.twitter.site) {
				pageMeta.twitter.site = '@sparefoot';
			}
			// Set twitter:image to match og:image
			if (!pageMeta.twitter.image) {
				pageMeta.twitter.image = pageMeta.og.image;
			}

			store.dispatch(setTags(pageMeta, noindex));
		}

		return next(action);
	};
